// Frontend-only model for multi-channel campaigns. Backed by stub data in
// `stub.ts` until the API exists; shaped so it can map onto a future
// `campaigns` + `campaign_channel_contents` schema without UI changes.

export type StudioChannel = "email" | "whatsapp" | "sms" | "push";

export const STUDIO_CHANNELS: StudioChannel[] = ["email", "whatsapp", "sms", "push"];

export type CampaignStatus = "draft" | "scheduled" | "sending" | "sent" | "paused";

// "all": send on every enabled channel the customer is reachable on.
// "fallback": try channels in `fallback_order`, stop at the first reachable one.
export type DeliveryStrategy = "all" | "fallback";

// ----------------------------
// Email
// ----------------------------

export type EmailAlign = "left" | "center" | "right";

export type EmailBlock =
  | { id: string; type: "heading"; text: string; level: 1 | 2; align: EmailAlign }
  | { id: string; type: "text"; text: string; align: EmailAlign }
  | { id: string; type: "image"; src: string; alt: string; href: string; width: number }
  | { id: string; type: "button"; label: string; href: string; align: EmailAlign; full_width: boolean }
  | { id: string; type: "coupon"; headline: string; show_qr: boolean; show_expiry: boolean }
  | { id: string; type: "products"; product_ids: string[]; columns: 2 | 3 }
  | { id: string; type: "divider" }
  | { id: string; type: "spacer"; height: number }
  | { id: string; type: "social"; instagram: string; facebook: string; website: string }
  | { id: string; type: "footer"; text: string };

export type EmailBlockType = EmailBlock["type"];

export type EmailTheme = {
  brand_color: string;
  background_color: string;
  content_background: string;
  text_color: string;
  font: "sans" | "serif" | "rounded";
  radius: number;
};

export type EmailContent = {
  from_name: string;
  reply_to: string;
  subject: string;
  preheader: string;
  theme: EmailTheme;
  blocks: EmailBlock[];
};

// ----------------------------
// WhatsApp (Meta template messages)
// ----------------------------

export type WhatsAppHeader =
  | { type: "none" }
  | { type: "text"; text: string }
  | { type: "image"; src: string };

export type WhatsAppButton =
  | { id: string; type: "quick_reply"; label: string }
  | { id: string; type: "url"; label: string; url: string }
  | { id: string; type: "copy_code"; label: string };

export type TemplateApprovalStatus = "not_submitted" | "pending" | "approved" | "rejected";

export type WhatsAppContent = {
  template_name: string;
  language: string;
  header: WhatsAppHeader;
  body: string;
  footer: string;
  buttons: WhatsAppButton[];
  approval_status: TemplateApprovalStatus;
};

// ----------------------------
// SMS
// ----------------------------

export type SmsContent = {
  sender_id: string;
  body: string;
  shorten_links: boolean;
  append_opt_out: boolean;
};

// ----------------------------
// Push (wallet pass notification)
// ----------------------------

export type PushContent = {
  title: string;
  body: string;
  update_pass_offer: boolean;
};

// ----------------------------
// Offer + schedule
// ----------------------------

export type CampaignOffer =
  | { type: "none" }
  | {
      type: "coupon";
      reward: "percentage" | "fixed" | "free_item";
      value: number;
      product_id: string | null;
      min_spend: number | null;
      expires_in_days: number | null;
      single_use: boolean;
    };

export type CampaignSchedule =
  | { type: "now" }
  | { type: "later"; send_at: string; send_in_customer_timezone: boolean }
  | { type: "recurring"; frequency: "weekly" | "monthly"; weekday: number; time: string };

// ----------------------------
// Campaign
// ----------------------------

export type CampaignStats = {
  targeted: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  redeemed: number;
  revenue: number; // cents
  unsubscribed: number;
  by_channel: Partial<Record<StudioChannel, { sent: number; delivered: number; opened: number; clicked: number }>>;
};

export type StudioCampaign = {
  id: string;
  name: string;
  status: CampaignStatus;
  segment_id: string;
  channels: StudioChannel[];
  strategy: DeliveryStrategy;
  fallback_order: StudioChannel[];
  holdout_percent: number;
  offer: CampaignOffer;
  schedule: CampaignSchedule;
  email: EmailContent;
  whatsapp: WhatsAppContent;
  sms: SmsContent;
  push: PushContent;
  stats: CampaignStats | null;
  updated_at: string;
};

// ----------------------------
// Supporting stub entities
// ----------------------------

export type StudioSegment = {
  id: string;
  name: string;
  description: string;
  size: number;
  reachable: Record<StudioChannel, number>;
};

export type SampleCustomer = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  points_balance: number;
  favourite_item: string;
  locale: string;
};

export type StudioProduct = {
  id: string;
  name: string;
  price: number; // cents
  image: string;
};
